// Hooks extracted from SrDetailPage — SR mutations, SR query, carers query.

import { useQuery, useMutation, type UseMutationResult, type QueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api.js";
import type { CarerItem } from "../components/AssignCarerSection.js";
// Re-use the canonical SrDetail from SrDetailPage.components — the previous
// local copy here drifted (missing `category`, weaker service_type) and
// caused TS2741 at SrDetailPage call sites. Single source of truth now.
import type { SrDetail } from "../pages/SrDetailPage.components.js";

export type { CarerItem };

interface CarerListResponse { data: CarerItem[]; total: number; page: number; limit: number; }

export interface SrMutations {
  transition: UseMutationResult<unknown, Error, string>;
  assign: UseMutationResult<unknown, Error, string>;
  checkin: UseMutationResult<unknown, Error, void>;
  checkout: UseMutationResult<unknown, Error, void>;
  csat: UseMutationResult<unknown, Error, number>;
  famSat: UseMutationResult<unknown, Error, number>;
  patchSr: UseMutationResult<unknown, Error, Record<string, unknown>>;
  assignAgent: UseMutationResult<unknown, Error, { agent_id: string; agent_type: string }>;
}

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function useSrMutations(id: string, qc: QueryClient): SrMutations {
  const invalidate = (): void => { void qc.invalidateQueries({ queryKey: ["sr", id] }); };
  const transition = useMutation<unknown, Error, string>({
    mutationFn: (status: string) => apiFetch<unknown>(`/sr/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
    onSuccess: invalidate,
  });
  const assign = useMutation<unknown, Error, string>({
    mutationFn: (carer_id: string) => apiFetch<unknown>(`/sr/${id}/assign`, { method: "PATCH", body: JSON.stringify({ carer_id }) }),
    onSuccess: invalidate,
  });
  const checkin = useMutation<unknown, Error, void>({
    mutationFn: () => apiFetch<unknown>(`/sr/${id}/checkin`, { method: "PATCH", body: "{}" }),
    onSuccess: invalidate,
  });
  const checkout = useMutation<unknown, Error, void>({
    mutationFn: () => apiFetch<unknown>(`/sr/${id}/checkout`, { method: "PATCH", body: "{}" }),
    onSuccess: invalidate,
  });
  const csat = useMutation<unknown, Error, number>({
    mutationFn: (score) => apiFetch<unknown>(`/csat/sr/${id}`, { method: "PATCH", body: JSON.stringify({ score, channel: "manual" }) }),
    onSuccess: invalidate,
  });
  const famSat = useMutation<unknown, Error, number>({
    mutationFn: (rating) => apiFetch<unknown>(`/sr/${id}/family-satisfaction`, { method: "PATCH", body: JSON.stringify({ rating }) }),
    onSuccess: invalidate,
  });
  const patchSr = useMutation<unknown, Error, Record<string, unknown>>({
    mutationFn: (data) => apiFetch<unknown>(`/sr/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    onSuccess: invalidate,
  });
  const assignAgent = useMutation<unknown, Error, { agent_id: string; agent_type: string }>({
    mutationFn: (data) => apiFetch<unknown>(`/sr/${id}/assign-agent`, { method: "POST", body: JSON.stringify(data) }),
    onSuccess: invalidate,
  });
  return { transition, assign, checkin, checkout, csat, famSat, patchSr, assignAgent };
}

export function usesr(id: string): ReturnType<typeof useQuery<SrDetail>> {
  return useQuery<SrDetail>({
    queryKey: ["sr", id],
    queryFn: () => apiFetch<SrDetail>(`/sr/${id}`),
  });
}

export function useCarers(): ReturnType<typeof useQuery<CarerListResponse>> {
  return useQuery<CarerListResponse>({
    queryKey: ["carers"],
    queryFn: () => apiFetch<CarerListResponse>("/carers?limit=100"),
  });
}
